import { AxiosResponse } from 'axios';
import { COUNTRY } from '../locale';
import performanceStore from '@/stores/performance';
import { CustomPlugin, MdapSdk } from '@mdap/javascript-sdk';
import { PagePerformanceReportData, config } from './config';
import APIPlugin from '@mdap/sdk-plugin-api';
import PagePerfPlugin from '@mdap/sdk-plugin-performance';
import ResourcePlugin from '@mdap/sdk-plugin-resource';
import ExceptionPlugin from '@mdap/sdk-plugin-exception';

function getPerformanceReportData(): Partial<PagePerformanceReportData> {
  const { first_paint, first_interactive, api_request } = performanceStore;
  const performance = window.performance || {};
  const time = performance.timing || {};
  const paint = performance?.getEntriesByType?.('paint');

  return {
    user_defined_first_paint: first_paint,
    user_defined_first_interactive: first_interactive,
    user_defined_first_api: api_request,
    first_paint: paint?.find((v) => v.name === 'first-paint')?.startTime,
    first_contentful_paint: paint?.find((v) => v.name === 'first-contentful-paint')?.startTime,
    ttfb: time.responseStart - time.navigationStart,
    response_end: time.responseEnd - time.navigationStart,
    dom_loading: time.domLoading - time.navigationStart,
    dom_interactive: time.domInteractive - time.navigationStart,
    dom_content_loaded_event_end: time.domContentLoadedEventEnd - time.navigationStart,
    dom_complete: time.domComplete - time.navigationStart,
    redirect: time.redirectEnd - time.redirectStart,
    app_cache: time.domainLookupStart - time.fetchStart,
    dns: time.domainLookupEnd - time.domainLookupStart,
    tcp: time.connectEnd - time.connectStart,
    stalled: time.requestStart - time.connectStart,
  };
}

class MDAPReporter {
  private inited = false;
  private queue: (() => void)[] = []; // SDK初始化之前累积的上报事件队列
  private customReporter?: CustomPlugin;
  apiPlugin?: APIPlugin;

  async lazyInit() {
    // 初始化SDK
    const sdk = new MdapSdk({
      // mdap 项目id
      app_name: config.appName,
      secret_key: '',
      // 上报环境 test | staging | live
      // 此参数是初始化MDAP SDK环境，例如 test上报至MDAP test环境数据库，需要在mdap.test.shopee.io/index路径下查看,
      // 由于MDAP环境有不稳定的情况，所以目前只使用live环境
      environment: 'live',
      // 上报地区 | sg | tw | ph | th | id | my | br | mx | ...
      region: COUNTRY,
      // 是否开启调试日志 | default - false
      logger: false,
      // 业务应用版本
      app_version: 'v0.1.0',
      //  采样率
      sample: 1,
    });

    // api请求监控插件
    const apiPlugin = new APIPlugin({ path: ['/api/((?!v1/heartbeat$).)*$', '/chat/((?!v1/heartbeat$).)*$', /\/bff\//], useLogicStatus: true });
    this.apiPlugin = apiPlugin;

    // 静态资源加载监控插件
    const resourcePlugin = new ResourcePlugin({ path: ['/static'] });

    // 页面性能监控插件
    const pagePerfPlugin = new PagePerfPlugin({ path: ['/'] });

    // JS 异常信息上报
    const exceptionPlugin = new ExceptionPlugin();

    // 自定义上报监控插件
    this.customReporter = new CustomPlugin({});

    sdk.use(apiPlugin);
    sdk.use(resourcePlugin);
    sdk.use(pagePerfPlugin);
    sdk.use(exceptionPlugin);
    sdk.use(this.customReporter);

    this.inited = true;

    this.queue.forEach((v) => v.call(this));
  }

  private handleSendEvent(sendFunction: () => void) {
    if (this.inited) {
      sendFunction();
      return;
    }

    this.queue.push(sendFunction);
  }

  sendPagePerformance = () =>
    this.handleSendEvent(() => {
      const data = getPerformanceReportData();
      (Object.keys(data) as (keyof PagePerformanceReportData)[]).forEach((v) => {
        if (data[v] !== undefined) {
          this.customReporter!.sendData({ point_id: config.pagePointIds[v], duration: Math.floor(data[v] as number) });
        }
      });
    });

  sendPvCount = () => this.handleSendEvent(() => this.customReporter!.sendData({ point_id: config.pvPointId }));
}

export const mdapReporter = new MDAPReporter();

export const mdapResponseInterceptors = (response: AxiosResponse) => {
  try {
    const mdapDataId = response?.request?._MDAP_DATA_ID_;
    if (mdapDataId && typeof response?.data?.code === 'number') {
      mdapReporter?.apiPlugin?.setLogicStatus?.(mdapDataId, response?.data?.code);
    }
  } catch (e) {}
};
